import { DisableOverflowScrolling } from '@assets/components/DisableOverflowScrolling';
import { TermsDocuments } from '@assets/components/TermsDocuments';
import type { ChangeEvent, FC, MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Button, Checkbox, TextInput } from 'react-materialize';
import { useMediaQuery } from 'react-responsive';
import { useNameForm } from './useNameForm';

export const NameForm: FC = () => {
	const isNotMobile = useMediaQuery({ query: '(min-width: 768.1px)' })
		? 'input-outlined'
		: '';

	const {
		formik,
		rulesCheckbox,
		setRulesCheckbox,
		isPhysicalPerson,
		lgpdFlowEnabled,
		handleFieldBlur,
		isSubmitDisabled,
	} = useNameForm();

	// Modo business: há um único campo, então ele deve iniciar como touched
	// para exibir validação (diferente de PF, que tem nome e sobrenome).
	const ensureFirstNameTouchedForBusiness = useCallback(() => {
		if (!isPhysicalPerson && !formik.touched.firstName) {
			formik.setFieldTouched('firstName', true, true);
		}
	}, [formik, isPhysicalPerson]);

	const handleFirstNameChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			formik.handleChange(e);
			ensureFirstNameTouchedForBusiness();
		},
		[formik, ensureFirstNameTouchedForBusiness],
	);

	const handleLastNameChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			formik.handleChange(e);
		},
		[formik],
	);

	const handleRulesCheckboxChange = useCallback(
		(e: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>) => {
			const target = e.target as HTMLInputElement;
			setRulesCheckbox(Boolean(target.checked));
		},
		[setRulesCheckbox],
	);

	const handleFirstNameBlur = useMemo(
		() => handleFieldBlur('firstName', formik),
		[handleFieldBlur, formik],
	);

	const handleLastNameBlur = useMemo(
		() => handleFieldBlur('lastName', formik),
		[handleFieldBlur, formik],
	);

	// Função interna para renderizar o checkbox LGPD
	const renderLgpdCheckbox = () => {
		if (!lgpdFlowEnabled) return null;

		return (
			<Checkbox
				className='rounded_checkbox'
				checked={rulesCheckbox}
				filledIn
				label=''
				onChange={handleRulesCheckboxChange}
			/>
		);
	};

	return (
		<form
			onSubmit={formik.handleSubmit}
			className='roll_in_from_bottom_animation full_page_form'
		>
			<Helmet>
				<title>Informe seu nome</title>
				<meta
					name='description'
					content={`Solicitação do ${isPhysicalPerson ? 'nome' : 'razão social'}.`}
				/>
			</Helmet>

			{/* biome-ignore lint/correctness/useUniqueElementIds: ID semântico e componente único na página */}
			<TextInput
				id='firstName'
				name='firstName'
				label={`${isPhysicalPerson ? 'Primeiro nome' : 'Razão social'}`}
				className={`${isNotMobile} ${(formik.touched.firstName && Boolean(formik.errors.firstName) && 'invalid') || ''}`}
				value={formik.values.firstName}
				onBlur={handleFirstNameBlur}
				onChange={handleFirstNameChange}
				error={(formik.touched.firstName && formik.errors.firstName) || ' '}
				disabled={formik.isSubmitting}
			/>

			{isPhysicalPerson && (
				<>
					{/* biome-ignore lint/correctness/useUniqueElementIds: ID semântico e componente único na página */}
					<TextInput
						id='lastName'
						name='lastName'
						label='Sobrenome'
						className={`${isNotMobile} ${(formik.touched.lastName && Boolean(formik.errors.lastName) && 'invalid') || ''}`}
						value={formik.values.lastName}
						onBlur={handleLastNameBlur}
						onChange={handleLastNameChange}
						error={(formik.touched.lastName && formik.errors.lastName) || ' '}
						disabled={formik.isSubmitting}
					/>
				</>
			)}

			<div
				className='info'
				style={{
					textAlign: lgpdFlowEnabled ? 'start' : 'center',
				}}
			>
				{renderLgpdCheckbox()}

				<TermsDocuments />
			</div>

			{/* Submit */}
			<Button
				flat
				type='submit'
				disabled={isSubmitDisabled()}
				waves='light'
				className='white-text'
			>
				<DisableOverflowScrolling />
				Continuar
			</Button>
		</form>
	);
};
